// client/src/components/company/CompanyProfile.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { User, UserCheck, Building, Bell, FileText } from 'lucide-react';

const CompanyProfile = () => {
  const { t, i18n } = useTranslation(); // ✅ Aggiungere i18n
  const currentLanguage = i18n.language.startsWith('it') ? 'it' : 'en'; // ✅ Aggiungere questa riga
  
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailBidNotifications, setEmailBidNotifications] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [preferredLanguage, setPreferredLanguage] = useState('it');
  
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get('/api/auth/profile');
        setUser(res.data.user);
        setOrganization(res.data.user.organization);
        setPreferredLanguage(res.data.user.preferredLanguage || 'it');
        
        // If user has notification preferences stored, use them
        if (res.data.user.emailPreferences) {
          // For companies, bidApprovals is the relevant preference for new bids
          setEmailBidNotifications(res.data.user.emailPreferences.bidApprovals !== false);
        }
        
        setLoading(false);
      } catch (err) {
        setError(t('common.error.fetchProfile', { 
          defaultValue: 'Failed to fetch profile' 
        }));
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [t]);
  
  // Save notification settings
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    
    setSavingSettings(true);
    setSaveSuccess(false);
    
    try {
      // Update email preferences using the profile update endpoint
      await axios.put('/api/auth/profile', {
        preferredLanguage: preferredLanguage,
        emailPreferences: {
          // Keep liquidityRequests value if it exists, or default to true
          liquidityRequests: user?.emailPreferences?.liquidityRequests !== false,
          // Update bidApprovals based on checkbox (this handles bid notifications for companies)
          bidApprovals: emailBidNotifications
        }
      });
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(t('common.error.saveSettings', { 
        defaultValue: 'Failed to save notification settings' 
      }));
    } finally {
      setSavingSettings(false);
    }
  };
  
  if (loading) {
    return <div className="loading">{t('common.loadingProfile')}</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  return (
    <div className="company-dashboard">
      {/* Profile Information */}
      <div className="card">
        <div className="card-title">
          <User className="card-title-icon" size={20} />
          <span>{t('profile.activeUser')}</span>
        </div>
        <div style={{ padding: '10px 0', textAlign: 'left' }}>
          <h3 style={{ margin: '0 0 10px 0', color: 'var(--dark)' }}>
            {user?.firstName} {user?.lastName}
          </h3>
          <h3 style={{ margin: '0 0 10px 0', color: 'var(--dark)' }}>
            {organization?.name} - {user?.position}
          </h3>
          <p style={{ 
            marginTop: '15px', 
            fontSize: '0.9em', 
            color: 'var(--grey)', 
            fontStyle: 'italic' 
          }}>
            {t('profile.contactForChanges')}
          </p>
        </div>
      </div>
      
      <div className="card company-dashboard">
        <div className="card-title">
          <UserCheck className="card-title-icon" size={20} />
          <span>{t('profile.userInformation')}</span>
        </div>
        <form id="profile-form">
          <div className="form-group">
            <label htmlFor="first-name">{t('profile.firstName')}</label>
            <input type="text" id="first-name" value={user?.firstName} disabled />
          </div>
          <div className="form-group">
            <label htmlFor="last-name">{t('profile.lastName')}</label>
            <input type="text" id="last-name" value={user?.lastName} disabled />
          </div>
          <div className="form-group">
            <label htmlFor="email">{t('profile.emailAddress')}</label>
            <input type="email" id="email" value={user?.email} disabled />
          </div>
          <div className="form-group">
            <label htmlFor="phone">{t('profile.phoneNumber')}</label>
            <input type="tel" id="phone" value={user?.phone} disabled />
          </div>
          <div className="form-group">
            <label htmlFor="role">{t('profile.role')}</label>
            <input type="text" id="role" value={user?.position} disabled />
          </div>
        </form>
      </div>
      
      <div className="card company-dashboard">
        <div className="card-title">
          <Building className="card-title-icon" size={20} />
          <span>{t('profile.companyInformation')}</span>
        </div>
        <form id="company-form">
          <div className="form-group">
            <label htmlFor="company-name">{t('profile.companyName')}</label>
            <input type="text" id="company-name" value={organization?.name} disabled />
          </div>
          
          {/* ✅ NUOVO: Campo P.IVA aggiunto dopo Company Name */}
          <div className="form-group">
            <label htmlFor="vat-number">{t('auth.register.vatNumber')}</label>
            <input 
              type="text" 
              id="vat-number" 
              value={organization?.vatNumber || ''} 
              disabled 
              placeholder={organization?.vatNumber ? '' : 'Non disponibile'}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="company-address">{t('profile.address')}</label>
            <input 
              type="text" 
              id="company-address" 
              value={organization?.address ? 
                `${organization.address.street}, ${organization.address.city}, ${organization.address.postcode}, ${organization.address.country}` 
                : ''}
              disabled 
            />
          </div>
        </form>
      </div>
      
      <div className="card company-dashboard">
        <div className="card-title">
          <Bell className="card-title-icon" size={20} />
          <span>{t('profile.notificationSettings')}</span>
        </div>
        <form id="notification-form" onSubmit={handleSaveSettings}>
          <div className="form-group">
            <label htmlFor="preferredLanguage">{t('profile.preferredLanguage') || 'Preferred Language'}</label>
            <select
              id="preferredLanguage"
              value={preferredLanguage}
              onChange={(e) => setPreferredLanguage(e.target.value)}
              disabled={savingSettings}
              className="form-control"
            >
              <option value="it">Italiano</option>
              <option value="en">English</option>
            </select>
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                checked={emailBidNotifications} 
                onChange={(e) => setEmailBidNotifications(e.target.checked)} 
              />
              <span>{t('profile.emailNotificationsBids')}</span>
            </label>
          </div>
          
          <div className="form-actions" style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '15px',
            marginTop: '25px',
            paddingTop: '20px',
            borderTop: '1px solid var(--border)'
          }}>
            {saveSuccess && (
              <span className="success-message" style={{ color: 'var(--success)' }}>
                {t('profile.settingsSaved')}
              </span>
            )}
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={savingSettings}
            >
              {savingSettings ? t('profile.saving') : t('profile.saveSettings')}
            </button>
          </div>
        </form>
      </div>

      <div className="card company-dashboard">
        <div className="card-title">
          <FileText className="card-title-icon" size={20} />
          <span>{t('profile.legalDocuments')}</span>
        </div>
        <div className="legal-documents">
          <p>{t('profile.reviewDocuments')}</p>
          <div className="document-links" style={{ marginTop: '15px' }}>
            <div className="document-link" style={{ marginBottom: '10px' }}>
              <a 
                href={currentLanguage === 'en' ? "/Termini e Condizioni di Utilizzo_eng.pdf" : "/Termini e Condizioni di Utilizzo_ita.pdf"}
                target="_blank" 
                rel="noopener noreferrer" 
                className="primary-link"
                style={{ textDecoration: 'none', color: 'var(--primary)', fontWeight: '500' }}
              >
                📄 {t('profile.termsConditions')}
              </a>
            </div>
            <div className="document-link" style={{ marginBottom: '10px' }}>
              <a 
                href={currentLanguage === 'en' ? "/Informativa Privacy_eng.pdf" : "/Informativa Privacy_ita.pdf"}
                target="_blank" 
                rel="noopener noreferrer" 
                className="primary-link"
                style={{ textDecoration: 'none', color: 'var(--primary)', fontWeight: '500' }}
              >
                🔒 {t('profile.privacyPolicy')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;